import React from 'react';
import {
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Button,
} from '@material-ui/core';
import CopyableValue from './CopyableValue';
import StalePriceLastCollectedCell from './StalePriceLastCollectedCell';
import StalePriceReviewStatusCell from './StalePriceReviewStatusCell';
import StalePriceReportTypeChip from './StalePriceReportTypeChip';

export default function StalePriceStoreDetailTable({
  details,
  loading,
  onStatusSaved,
  onClearFilters,
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!details || details.length === 0) {
    return (
      <Box p={2} textAlign="center">
        <Typography variant="body2" color="textSecondary" gutterBottom>
          No store details found.
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
          Try adjusting your filters to see more results.
        </Typography>
        {onClearFilters && (
          <Button size="small" variant="outlined" onClick={onClearFilters}>
            Clear all filters
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Banner</TableCell>
          <TableCell style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Store</TableCell>
          <TableCell style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>City</TableCell>
          <TableCell style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>IDs</TableCell>
          <TableCell style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Last Collected</TableCell>
          <TableCell style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Review Status</TableCell>
          <TableCell style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Reports</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {details.map((detail) => (
          <TableRow
            key={detail.sighting_id}
            style={{
              backgroundColor: detail.has_valid_manual_check ? undefined : '#fff8f8',
            }}
          >
            <TableCell>
              <Typography variant="body2">{detail.banner_name}</Typography>
              {detail.last_report_type && (
                <StalePriceReportTypeChip reportType={detail.last_report_type} />
              )}
            </TableCell>
            <TableCell>
              <Typography variant="body2">{detail.location_name}</Typography>
              {detail.store_address && (
                <Typography variant="caption" display="block">
                  <i>{detail.store_address}</i>
                </Typography>
              )}
            </TableCell>
            <TableCell>{detail.city || '—'}</TableCell>
            <TableCell>
              <CopyableValue label="CP" value={detail.company_product_id} />
              <CopyableValue label="Store" value={detail.store_id} />
              <CopyableValue label="Postal" value={detail.store_postal} />
            </TableCell>
            <StalePriceLastCollectedCell row={detail} onStatusSaved={onStatusSaved} />
            <TableCell>
              <StalePriceReviewStatusCell row={detail} />
            </TableCell>
            <TableCell>{detail.user_report_count}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function recomputeStalePriceSummaryFromDetails(summary, details) {
  const statusCounts = {};
  let reviewedStores = 0;
  let needsReviewStores = 0;

  details.forEach((detail) => {
    if (detail.has_valid_manual_check) {
      reviewedStores += 1;
      const statusId = detail.manual_check_status_id;
      if (statusId != null) {
        statusCounts[statusId] = (statusCounts[statusId] || 0) + 1;
      }
    } else {
      needsReviewStores += 1;
    }
  });

  const storesReported = details.length;
  return {
    ...summary,
    stores_reported: storesReported,
    reviewed_stores: reviewedStores,
    needs_review_stores: needsReviewStores,
    review_pct:
      storesReported > 0 ? Math.round((reviewedStores / storesReported) * 100) : 0,
    status_counts: statusCounts,
  };
}
